import {
  Header,
  Sidebar,
  SidebarFooter,
  SidebarItem,
  SidebarPageWrapper,
} from '@trueprodigy/components';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import * as globalActions from '@actions/global';
import {
  isSidebarVisibleSelector,
  userRightsSelector,
  userStatusSelector,
} from '@selectors/global';
import SidebarAccountSettingsWidget from '@components/Sidebar/SidebarAccountSettingsWidget';
import SidebarRdsNameWidget from '@components/Sidebar/SidebarRdsNameWidget';
import SidebarCaseSettingWidget from '@components/Sidebar/SidebarCaseSettingWidget';
import rocketIcon from '@components/Sidebar/assets/rocket-icon.png';
import { canExecuteSubject } from '@config/permissions/Can';
import ability, { defineRules } from '@config/permissions/ability';
import BetaTag from '@components/BetaTag';
import processManagerLogo from '../assets/logo.png';
import { parseHeaderTitle, prefix } from '../routes';

const PageWrapper = ({
  children,
  location,
  toggleSidebar,
  isSidebarVisible,
  userStatus,
  userRights,
}) => {
  const isReadOnly = userStatus.get('readOnly');
  const userRightsJS = userRights.toJS();

  useEffect(() => {
    if (userRights?.toJS()?.length) {
      const rules = defineRules(userRights.toJS());
      ability.update(rules);
    }
  }, [userRights]);

  useEffect(() => {
    const isiPad = navigator.userAgent.match(/Mac/) && navigator.maxTouchPoints;
    if (isSidebarVisible && isiPad) {
      toggleSidebar();
    }
  }, [location.pathname]);

  return (
    <>
      <Helmet>
        <title>Prodigy Process Manager</title>
      </Helmet>

      <Sidebar
        isSidebarVisible={isSidebarVisible}
        logo={processManagerLogo}
        sidebarFooter={
          <SidebarFooter>
            <SidebarRdsNameWidget />
            <SidebarCaseSettingWidget />
            <SidebarAccountSettingsWidget />
          </SidebarFooter>
        }
      >
        <SidebarItem icon={rocketIcon} link="/" title="Prodigy App Launcher" />
        <PageWrapper.MenuTitle>App Menu</PageWrapper.MenuTitle>
        {canExecuteSubject('AppraisalMassRecalculation') && (
          <SidebarItem
            link={`${prefix}/mass-recalc`}
            title="Mass Recalculation"
          />
        )}
        <SidebarItem
          link={`${prefix}/recalc-errors`}
          title="Recalculation Errors"
        />
        <SidebarItem isOpen nested title="Reports">
          {isReadOnly ? (
            <></>
          ) : (
            <SidebarItem
              link={`${prefix}/appraisal-totals`}
              title="Appraisal Totals"
            />
          )}

          <SidebarItem
            link={`${prefix}/reprint-totals`}
            title="Appraisal Totals Reprint"
          />
          <SidebarItem
            link={`${prefix}/appraisal-roll`}
            title="Appraisal Roll"
          />
          {isReadOnly ? (
            <></>
          ) : (
            <SidebarItem
              link={`${prefix}/top-taxpayer-report`}
              title="Top Taxpayer Report"
            />
          )}
          {isReadOnly ? (
            <></>
          ) : (
            <SidebarItem
              link={`${prefix}/tirz-property-report`}
              title="TIRZ Property Report"
            />
          )}
        </SidebarItem>
        {canExecuteSubject('AppraisalBuildingPermitImport') && (
          <SidebarItem isOpen nested title="Building Permit">
            <SidebarItem
              title="Import Process"
              link={`${prefix}/building-permit-import-process`}
            />
            <SidebarItem
              title="Configurations"
              link={`${prefix}/building-permit-configurations`}
            />
            <SidebarItem
              title="Mass Delete"
              link={`${prefix}/building-permit-mass-delete`}
            />
          </SidebarItem>
        )}

        {canExecuteSubject('AppraisalMassCreateCompGrids') ? (
          <SidebarItem isOpen nested title="Grids">
            <SidebarItem
              title="Mass Generation"
              link={`${prefix}/mass-generation`}
            />
          </SidebarItem>
        ) : (
          <></>
        )}
        {process.env.LETTER_PROCESSING_ENABLED && (
          <SidebarItem
            isOpen
            nested
            title={
              <>
                Prodigy Letters <BetaTag />
              </>
            }
          >
            <SidebarItem
              link={`${prefix}/letter-maintenance`}
              title="Letter Maintenance"
            />
            <SidebarItem
              link={`${prefix}/mass-letter-runs`}
              title="Mass Letter Runs"
            />
          </SidebarItem>
        )}
        <SidebarItem
          link={`${prefix}/capture-january1-owner`}
          title="Capture Janurary 1 Owner"
        />
        <SidebarItem link={`${prefix}/auto-add-ov65`} title="Auto Add OV65" />
        <SidebarItem
          link={`${prefix}/tax-limitation-process`}
          title="Tax Limitation Process"
        />
        <SidebarItem isOpen nested title="Portal Communications">
          <SidebarItem
            title="PIN Retrieval Requests"
            link={`${prefix}/pin-retrieval-requests`}
          />
          <SidebarItem
            title="EDR - Sign Up Reminders"
            link={`${prefix}/electronic-delivery-requests-sign-up-reminders`}
          />
          <SidebarItem
            title="EDR - Portal Notifications"
            link={`${prefix}/electronic-delivery-requests-portal-notifications`}
          />
        </SidebarItem>
        <SidebarItem isOpen nested title="Appraisal Notice Process">
          <SidebarItem
            title="Appraisal Notices"
            link={`${prefix}/appraisal-notices`}
          />
          <SidebarItem
            title="Capture Submission Values "
            link={`${prefix}/capture-submission-values`}
          />
        </SidebarItem>
        <SidebarItem
          link={`${prefix}/certification-process`}
          title="Certification Process"
        />
        {userRightsJS.includes('AppraisalNewYearLayer') ? (
          <SidebarItem
            link={`${prefix}/new-year-layer`}
            title="New Year Layer"
          />
        ) : (
          <></>
        )}
        {/* <SidebarItem link={prefix + '/ptd-process'} title="PTD Process" /> */}
        <SidebarItem isOpen nested title="PTD Process">
          <SidebarItem
            title="PTD Integrity Checks"
            link={`${prefix}/ptd-process/integrity-checks`}
          />
          <SidebarItem
            title="EARS Submission"
            link={`${prefix}/ptd-process/ears-submission`}
          />
          <SidebarItem
            title="EPTS Submission"
            link={`${prefix}/ptd-process/epts-submission`}
          />
          {isReadOnly ? (
            <></>
          ) : (
            <SidebarItem
              title="PTD Configuration"
              link={`${prefix}/ptd-process/ptd-configuration`}
            />
          )}
          <SidebarItem
            title="PVS Tax Rate Submission"
            link={`${prefix}/ptd-process/pvs-tax-rate-submission`}
          />
        </SidebarItem>
        {userRightsJS.includes('AppraisalRollCorrectMass') ? (
          <SidebarItem isOpen nested title="Roll Correction">
            <SidebarItem
              link={`${prefix}/roll-correction-groups`}
              title="Roll Correction Manager"
            />
            <SidebarItem
              link={`${prefix}/roll-correction-jobs`}
              title="Mass Roll Correct"
            />
          </SidebarItem>
        ) : (
          <SidebarItem isOpen nested title="Roll Correction">
            <SidebarItem
              link={`${prefix}/roll-correction-groups`}
              title="Roll Correction Manager"
            />
          </SidebarItem>
        )}
        <SidebarItem isOpen nested title="Mass Update">
          {userRightsJS.includes('AppraisalMassUpdateMultiYearExecute') ? (
            <SidebarItem
              link={`${prefix}/mass-update-property-beta`}
              title="Mass Update Property - Multi Year"
            />
          ) : (
            <></>
          )}
          {userRightsJS.includes('AppraisalMassUpdateMultiYearExecute') ? (
            <SidebarItem
              link={`${prefix}/mass-update-other`}
              title="Mass Update - Other"
            />
          ) : (
            <></>
          )}
          {userRightsJS.includes('AppraisalMassCassValidation') ? (
            <SidebarItem
              link={`${prefix}/mass-cass-validation`}
              title="Mass CASS Certification"
            />
          ) : (
            <></>
          )}
          {userRightsJS.includes('AppraisalMassLinkSaleToDeed') ? (
            <SidebarItem
              link={`${prefix}/mass-match-sales-with-deeds`}
              title="Mass Match Sales with Deeds"
            />
          ) : (
            <></>
          )}
          <SidebarItem
            link={`${prefix}/mass-update-sales-runs`}
            title="Mass Update Sales Runs"
          />
        </SidebarItem>
        <SidebarItem isOpen nested title="Imports">
          <SidebarItem
            link={`${prefix}/mass-file-imports`}
            title="Mass File Imports"
          />
          <SidebarItem
            link={`${prefix}/mass-import-exemptions`}
            title="Mass Import Exemptions"
          />
          <SidebarItem
            link={`${prefix}/mineral-import`}
            title="Mineral Vendor Import"
          />
          <SidebarItem link={`${prefix}/sales-import`} title="Sales Import" />
        </SidebarItem>
        <SidebarItem isOpen nested title="Exports">
          <SidebarItem
            link={`${prefix}/exports/appraisal-data`}
            title="Appraisal Data"
          />
          <SidebarItem
            link={`${prefix}/exports/appraisal-json`}
            title="Appraisal Data - JSON"
          />
          <SidebarItem
            link={`${prefix}/exports/bpp-rendition`}
            title="BPP Rendition Export"
          />
          {userRightsJS.includes('HSAuditExportAdmin') ? (
            <SidebarItem
              link={`${prefix}/hs-audit-export`}
              title="HS Audit Export"
            />
          ) : (
            <></>
          )}
          <SidebarItem
            link={`${prefix}/exports/ownership-agent`}
            title="Ownership / Agent"
          />
        </SidebarItem>
        <SidebarItem link={`${prefix}/process-ex-366`} title="Process EX 366" />
        <SidebarItem isOpen nested title="BPP Late File Penalty Process">
          <SidebarItem
            link={`${prefix}/process-bpp-late-penalty`}
            title="Penalty Process"
          />
          <SidebarItem
            link={`${prefix}/bpp-penalty-configuration`}
            title="Configuration"
          />
        </SidebarItem>
        <SidebarItem isOpen nested title="Just Appraised">
          <SidebarItem
            title="Deed Processing"
            link={`${prefix}/just-appraised/deed-processing`}
          />
        </SidebarItem>
        <SidebarItem
          link={`${prefix}/exemption-resets`}
          title="Exemption Reset Process"
        />
        <SidebarItem title="Integrity Check Process" nested>
          <SidebarItem
            link={`${prefix}/integrity-checks`}
            title="Integrity Checks"
          />
          {isReadOnly ? (
            <></>
          ) : (
            <SidebarItem
              link={`${prefix}/integrity-check-configuration`}
              title="Integrity Check Configuration"
            />
          )}
        </SidebarItem>
      </Sidebar>
      <PageWrapper.ContentWrapper>
        <SidebarPageWrapper isSidebarVisible={isSidebarVisible}>
          <Header
            title={parseHeaderTitle(location.pathname)}
            toggleSidebar={toggleSidebar}
          />
          <div>{children}</div>
        </SidebarPageWrapper>
      </PageWrapper.ContentWrapper>
    </>
  );
};

PageWrapper.propTypes = {
  activeReport: PropTypes.object,
  children: PropTypes.any,
  history: PropTypes.any,
  location: PropTypes.any,
  toggleSidebar: PropTypes.func,
  isSidebarVisible: PropTypes.bool,
  userRights: PropTypes.any,
  userStatus: PropTypes.any,
};

PageWrapper.MenuTitle = styled.div`
  height: 35px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.4);
  background-color: #fff;
  color: #2f3e9e;
  font-size: 14px;
  padding: 8px 26px;
  font-weight: normal;
`;

PageWrapper.ContentWrapper = styled.div`
  > div {
    margin-top: -67px;
    min-height: 100vh;
    background-color: #f2f4f6;
  }
`;

const mapStateToProps = (state) => ({
  isSidebarVisible: isSidebarVisibleSelector(state),
  userRights: userRightsSelector(state),
  userStatus: userStatusSelector(state),
});

const mapDispatchToProps = {
  toggleSidebar: globalActions.toggleSidebar,
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(PageWrapper),
);
